/**
 * Safe number formatting utilities
 * Prevents runtime errors from undefined/null/NaN values
 */

/**
 * Safely format a number as currency (EUR)
 * @param {number|undefined|null} value - The value to format
 * @param {string} fallback - Fallback string if value is invalid (default: "—")
 * @returns {string} Formatted currency string or fallback
 */
export const formatCurrency = (value, fallback = "—") => {
  // Check if value is a valid finite number
  if (typeof value !== 'number' || !isFinite(value)) {
    return fallback;
  }
  
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch (error) {
    console.warn('Currency formatting error:', error);
    return fallback;
  }
};

/**
 * Safely format a number without currency symbol
 * @param {number|undefined|null} value - The value to format
 * @param {string} fallback - Fallback string if value is invalid (default: "—")
 * @returns {string} Formatted number string or fallback
 */
export const formatNumber = (value, fallback = "—") => {
  // Check if value is a valid finite number
  if (typeof value !== 'number' || !isFinite(value)) {
    return fallback;
  }
  
  try {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch (error) {
    console.warn('Number formatting error:', error);
    return fallback;
  }
};

/**
 * Safely format a percentage
 * @param {number|undefined|null} value - The value to format (as decimal, e.g., 0.07 for 7%)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @param {string} fallback - Fallback string if value is invalid (default: "—")
 * @returns {string} Formatted percentage string or fallback
 */
export const formatPercent = (value, decimals = 1, fallback = "—") => {
  // Check if value is a valid finite number
  if (typeof value !== 'number' || !isFinite(value)) {
    return fallback;
  }
  
  try {
    return `${(value * 100).toFixed(decimals)}%`;
  } catch (error) {
    console.warn('Percentage formatting error:', error);
    return fallback;
  }
};

/**
 * Safely get a numeric value with fallback
 * @param {number|undefined|null} value - The value to validate
 * @param {number} fallback - Fallback number if value is invalid (default: 0)
 * @returns {number} Valid number or fallback
 */
export const safeNumber = (value, fallback = 0) => {
  if (typeof value === 'number' && isFinite(value)) {
    return value;
  }
  return fallback;
};
